import { useEffect, useRef, useState } from 'react';
import {
  getEmbeddedFirmwareAvailability,
  inspectFirmwareFile,
  postNewFirmware,
} from '../../api/api';
import { IFirmwareFileInspection } from '../../api/types';
import { Button } from '../Common/Button';
import { ProductParams } from '../Product/Product';
import { getDeviceType } from '../../Types';
import { getFirmwareFamily } from '../../api/rules';
import { FirmwareInspectionPanel } from './FirmwareInspectionPanel';
import { EmbeddedFirmwareBrowser } from './EmbeddedFirmwareBrowser';

enum SubmissionStatus {
  Idle,
  Uploading,
  Success,
  Fail,
}

interface IFirmwareFamilyCheckboxProps {
  productCode: string;
  variant: string;
  useFirmwareFamily: boolean;
  setUseFirmwareFamily: (value: boolean) => void;
}

function FirmwareFamilyCheckbox({
  productCode,
  variant,
  useFirmwareFamily,
  setUseFirmwareFamily,
}: IFirmwareFamilyCheckboxProps) {
  const firmwareFamily = getFirmwareFamily({ productCode, variant });
  const othersInFamily = firmwareFamily
    .filter(
      (it) =>
        it.productCode !== productCode || it.variant !== variant,
    )
    .map((it) => `p${it.productCode}_v${it.variant}`)
    .join(', ');

  return firmwareFamily.length > 1 ? (
    <div className="flex-row space-x-3">
      <input
        id="firmware-family-upload"
        checked={useFirmwareFamily}
        type="checkbox"
        onChange={() => setUseFirmwareFamily(!useFirmwareFamily)}
      />
      <label htmlFor="firmware-family-upload">{`Upload to others in firmware family: ${othersInFamily}`}</label>
    </div>
  ) : (
    <div />
  );
}
export type FormFileUploaderParams = ProductParams & {
  succesfullyUploadedCallback: () => void;
};
function FormFileUploader(params: FormFileUploaderParams) {
  const { productCode, variant, succesfullyUploadedCallback } =
    params;
  const [file, setFile] = useState<File>();
  const [firmwareNumber, setFirmwareNumber] = useState<string>('');
  const [firmwareSemantic, setFirmwareSemantic] =
    useState<string>('');
  const [parsedMetadata, setParsedMetadata] =
    useState<IFirmwareFileInspection | null>(null);
  const [metadataMessage, setMetadataMessage] = useState<string>('');
  const [responseMessage, setResponseMessage] = useState<string>('');
  const [useFirmwareFamily, setUseFirmwareFamily] =
    useState<boolean>(true);
  const [submissionStatus, setSubmissionStatus] =
    useState<SubmissionStatus>(SubmissionStatus.Idle);
  const [uploadMethod, setUploadMethod] = useState<
    'file' | 'embedded'
  >('file');
  const [isEmbeddedFirmwareAvailable, setEmbeddedFirmwareAvailable] =
    useState(false);
  const inspectionRequestId = useRef(0);

  useEffect(() => {
    let isMounted = true;

    void getEmbeddedFirmwareAvailability(productCode, variant).then(
      (result) => {
        if (!isMounted) return;
        const isAvailable = result.status === 'OK' && result.data;
        setEmbeddedFirmwareAvailable(isAvailable);
        if (!isAvailable) setUploadMethod('file');
      },
    );

    return () => {
      isMounted = false;
    };
  }, [productCode, variant]);

  async function handleFileChange(selectorFiles: FileList | null) {
    const requestId = ++inspectionRequestId.current;
    setFile(undefined);
    setFirmwareNumber('');
    setFirmwareSemantic('');
    setParsedMetadata(null);
    setMetadataMessage('');
    setResponseMessage('');
    setUseFirmwareFamily(true);
    setSubmissionStatus(SubmissionStatus.Idle);

    if (!selectorFiles || selectorFiles.length === 0) return;

    const selectedFile = selectorFiles[0];
    setFile(selectedFile);
    setMetadataMessage('Inspecting firmware file...');

    try {
      const result = await inspectFirmwareFile(
        productCode,
        variant,
        selectedFile,
      );

      if (requestId !== inspectionRequestId.current) return;

      if (result.status === 'OK') {
        setParsedMetadata(result.data);
        setFirmwareNumber(
          result.data.suggestedFirmwareVersion?.toString() || '',
        );
        setFirmwareSemantic(
          result.data.suggestedSemanticVersion || '',
        );
        setMetadataMessage(
          `Detected ${result.data.format}. Values used by FWM remain editable.`,
        );
      } else {
        setMetadataMessage(
          'The file could not be inspected. Enter the FWM release values manually.',
        );
      }
    } catch {
      if (requestId !== inspectionRequestId.current) return;
      setMetadataMessage(
        'Could not inspect the file. Enter the FWM release values manually.',
      );
    }
  }

  function validateSemanticVersion(semanticVersion: string): boolean {
    return /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(-[a-zA-Z]+\d*)?$/.test(
      semanticVersion,
    );
  }

  function validateFirmwareNumber(firmwareNumber: string): boolean {
    return /^[1-9]\d*$/.test(firmwareNumber);
  }

  function inputValidationFeedback() {
    if (!file) return 'Please choose a firmware file';
    if (!firmwareNumber || !firmwareSemantic)
      return 'Enter both FWM release values';
    if (firmwareNumber && !validateFirmwareNumber(firmwareNumber))
      return 'Invalid firmware version number';
    if (firmwareSemantic && firmwareSemantic.startsWith('v'))
      return 'erroronous user, replace user\n or consider removing that v in front of the semantic version';
    if (
      firmwareSemantic &&
      !validateSemanticVersion(firmwareSemantic)
    )
      return 'Invalid semantic version';
    return 'OK';
  }

  const isFormReady = inputValidationFeedback() === 'OK';
  const hasMetadataOverride =
    parsedMetadata !== null &&
    ((parsedMetadata.suggestedFirmwareVersion !== null &&
      firmwareNumber !== '' &&
      Number(firmwareNumber) !==
        parsedMetadata.suggestedFirmwareVersion) ||
      (parsedMetadata.suggestedSemanticVersion !== null &&
        firmwareSemantic !== '' &&
        firmwareSemantic !==
          parsedMetadata.suggestedSemanticVersion));

  function handleSubmit() {
    console.log(file);
    if (!file || !firmwareNumber || !firmwareSemantic) return;
    console.log('Uploading...');
    setResponseMessage('Uploading...');
    setSubmissionStatus(SubmissionStatus.Uploading);

    const productsReceivingUpload = useFirmwareFamily
      ? getFirmwareFamily({ productCode, variant })
      : [{ productCode, variant }];

    Promise.all(
      productsReceivingUpload.map((it) =>
        postNewFirmware(
          it.productCode,
          it.variant,
          firmwareNumber,
          firmwareSemantic,
          file,
        ),
      ),
    ).then(
      (results) => {
        console.log(`Uploaded:`, results);
        setSubmissionStatus(SubmissionStatus.Success);
        setResponseMessage('Upload Success!');
        succesfullyUploadedCallback();
      },
      (rejects) => {
        console.log(`Failed uploaded:`, rejects);
        setSubmissionStatus(SubmissionStatus.Fail);
        setResponseMessage(
          rejects.status === 'Forbidden'
            ? "You don't have permission to upload firmware."
            : rejects.errorMessages.join(', '),
        );
      },
    );
  }

  return (
    <div>
      {isEmbeddedFirmwareAvailable && (
        <div
          className="flex flex-row gap-2 mt-2 mb-3"
          aria-label="Firmware source options"
        >
          <button
            type="button"
            aria-pressed={uploadMethod === 'file'}
            className={`px-3 py-2 rounded ${uploadMethod === 'file' ? 'bg-blue-700 text-white' : 'bg-gray-200'}`}
            onClick={() => setUploadMethod('file')}
          >
            Upload file
          </button>
          <button
            type="button"
            aria-pressed={uploadMethod === 'embedded'}
            className={`px-3 py-2 rounded ${uploadMethod === 'embedded' ? 'bg-blue-700 text-white' : 'bg-gray-200'}`}
            onClick={() => setUploadMethod('embedded')}
          >
            Browse Embedded S3
          </button>
        </div>
      )}
      {uploadMethod === 'embedded' && isEmbeddedFirmwareAvailable ? (
        <EmbeddedFirmwareBrowser
          productCode={productCode}
          variant={variant}
          succesfullyUploadedCallback={succesfullyUploadedCallback}
        />
      ) : (
        <div className="file-uploader flex flex-col space-y-3 mt-2">
          <h1 className="w-full mb-2 font-bold text-xl">
            Upload Firmware
          </h1>
          <div className="flex flex-row space-x-7">
            <label>
              Device
              <br />
              <b>{getDeviceType(productCode)}</b>
            </label>
            <label>
              Product code
              <br />
              <b className="flex flex-row-reverse">{productCode}</b>
            </label>
            <label>
              Variant
              <br />
              <b className="flex flex-row-reverse">{variant}</b>
            </label>
          </div>
          <label htmlFor="firmware-file">
            Firmware binary (.dat)
          </label>
          <input
            id="firmware-file"
            type="file"
            onChange={(e) => handleFileChange(e.target.files)}
            accept=".dat"
          />
          {metadataMessage && (
            <p className="text-sm text-gray-500 dark:text-gray-300">
              {metadataMessage}
            </p>
          )}
          {parsedMetadata && (
            <FirmwareInspectionPanel inspection={parsedMetadata} />
          )}
          {hasMetadataOverride && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              Warning: the entered FWM values override metadata parsed
              from the uploaded file.
            </p>
          )}
          <label>
            FWM Firmware Version Number (parsed and editable):
            <br />
            <input
              aria-label="Enter Firmware Version Number:"
              className="form-control block w-full mt-1 px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none"
              type="text"
              value={firmwareNumber}
              onChange={(e) => setFirmwareNumber(e.target.value)}
            />
          </label>
          <label>
            FWM Semantic Firmware Version (parsed and editable):
            <br />
            <input
              aria-label="Enter Semantic Firmware Version:"
              className="form-control block w-full mt-1 px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none"
              type="text"
              value={firmwareSemantic}
              onChange={(e) => setFirmwareSemantic(e.target.value)}
            />
          </label>
          <p
            className="text-sm text-gray-500 dark:text-gray-300"
            id="file_input_help"
          >
            Supported: dat
          </p>
          <FirmwareFamilyCheckbox
            productCode={productCode}
            variant={variant}
            useFirmwareFamily={useFirmwareFamily}
            setUseFirmwareFamily={setUseFirmwareFamily}
          />
          <Button
            onClick={handleSubmit}
            text="Upload"
            disabled={[
              {
                disabled:
                  !isFormReady ||
                  submissionStatus == SubmissionStatus.Success ||
                  submissionStatus == SubmissionStatus.Fail,
                reason: inputValidationFeedback(),
              },
              {
                disabled:
                  submissionStatus == SubmissionStatus.Uploading,
                reason: 'Working...',
              },
            ]}
          />
          <span>{responseMessage}</span>
        </div>
      )}
    </div>
  );
}

export default FormFileUploader;
